//
//  NotificationBell.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 13, 2026
//  Author: Bob Keathley
//
//  Knock in-app notification bell. KnockStack is the real provider stack;
//  tests substitute a stub and never reach Knock.
//
import { useRef, useState, type ComponentType, type ReactNode } from 'react'
import { KnockFeedProvider, KnockProvider, NotificationFeedPopover, NotificationIconButton } from '@knocklabs/react'
import '@knocklabs/react/dist/index.css'
import type { Session } from '@shared/auth'
import { KNOCK_FEED_CHANNEL_ID, KNOCK_PUBLIC_KEY, feedAllowed, useKnockToken } from '../knock'

type ProviderProps = { apiKey: string; userId: string; userToken: string; channelId: string; children: ReactNode }

/** The real Knock provider stack. Tests substitute a stub; they never reach Knock. */
export function KnockStack({ apiKey, userId, userToken, channelId, children }: ProviderProps) {
  return (
    <KnockProvider apiKey={apiKey} user={{ id: userId }} userToken={userToken}>
      <KnockFeedProvider feedId={channelId}>{children}</KnockFeedProvider>
    </KnockProvider>
  )
}

function BellPopover() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement | null>(null)
  return (
    <>
      <NotificationIconButton ref={ref} onClick={() => setOpen((o) => !o)} />
      <NotificationFeedPopover buttonRef={ref} isVisible={open} onClose={() => setOpen(false)} />
    </>
  )
}

/** Presentational: a ready token renders the bell inside `Provider`. */
export function BellView({
  apiKey, channelId, userId, token, Provider = KnockStack, children,
}: {
  apiKey: string; channelId: string; userId: string; token: string
  Provider?: ComponentType<ProviderProps>
  children?: ReactNode
}) {
  return (
    <span className="bell" aria-label="Notifications">
      <Provider apiKey={apiKey} userId={userId} userToken={token} channelId={channelId}>
        {children ?? <BellPopover />}
      </Provider>
    </span>
  )
}

/**
 * Hidden (renders nothing) when the public key is unset, in an assumed session, or when the token
 * call fails (503 notifications_disabled included). No toast, no error text.
 */
export default function NotificationBell({ session }: { session: Session }) {
  if (!feedAllowed(session)) return null
  return <Gated session={session} />
}

function Gated({ session }: { session: Session }) {
  const t = useKnockToken(session)
  if (t.kind !== 'ready' || !KNOCK_PUBLIC_KEY) return null
  return <BellView apiKey={KNOCK_PUBLIC_KEY} channelId={KNOCK_FEED_CHANNEL_ID ?? ''} userId={t.userId} token={t.token} />
}
